import type React from "react";
import { useRouter } from "solito/router";
import { UserCircle } from "@tamagui/lucide-icons";
import { FullscreenSpinner, GrantedTextLogo, Paragraph, Separator, type StackProps, useMedia, validToken, XStack, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { useAuth } from "../../lib/auth";
import { useBoolVariation } from "../../lib/flag";
import { MobileNavPopover } from "./components/MobileNavPopover.web";
import { NavTabs } from "./components/NavTabs.web";
import { WebNavigation } from "./components/WebNavigation.web";
export type HomeLayoutProps = {
  children?: React.ReactNode;
  padded?: boolean;
  fullPage?: boolean;
  backgroundColor?: StackProps["backgroundColor"];
  hideTabs?: boolean;
  title?: string;
};
export const HomeLayout = ({
  children,
  fullPage = false,
  padded = false,
  backgroundColor,
  hideTabs = false
}: HomeLayoutProps) => {
  const {
    isReady,
    isAuthenticated
  } = useAuth();
  const router = useRouter();
  const media = useMedia();
  const useNewNavigation = useBoolVariation(Flag.NEW_WEB_NAVIGATION, false);
  if (!isReady) {
    return <FullscreenSpinner />;
  }
  if (useNewNavigation) {
    return <XStack f={1} height={validToken("100vh")} overflow="hidden" backgroundColor={backgroundColor}>
        {media.gtSm ? <WebNavigation /> : null}
        <YStack f={1} overflow="hidden">
          {!media.gtSm ? <YStack borderBottomWidth={1} borderBottomColor="$strokeSecondary" backgroundColor="$background" paddingHorizontal="$4" paddingVertical="$2.5">
              <XStack jc="space-between" ai="center">
                <YStack onPress={() => router.push("/")} cursor="pointer" justifyContent="center" alignItems="center" minHeight={54}>
                  <GrantedTextLogo width={80} height={16} />
                </YStack>
                <MobileNavPopover>
                  <WebNavigation />
                </MobileNavPopover>
              </XStack>
            </YStack> : null}
          <YStack {...fullPage && {
          flex: 1
        }} {...padded && {
          maxWidth: 800,
          mx: "auto",
          px: "$2",
          width: "100%"
        }} f={1} overflow="scroll">
            {children}
          </YStack>
        </YStack>
      </XStack>;
  }

  // Old top navigation
  return <YStack f={1} overflow="hidden"
  // This is needed to bottom sheets don't create extra space under the pages
  $platform-web={{
    height: "100vh"
  }} backgroundColor={backgroundColor} data-sentry-element="YStack" data-sentry-component="HomeLayout" data-sentry-source-file="layout.web.tsx">
      <YStack gap="$4" borderWidth="$0" borderBottomColor="$strokeSecondary" borderStyle="solid" borderBottomWidth="$0.5" jc="center" px="$4" backgroundColor="$color1" data-sentry-element="YStack" data-sentry-source-file="layout.web.tsx">
        <XStack jc="space-between" $sm={{
        py: "$2.5"
      }} ai="center" data-sentry-element="XStack" data-sentry-source-file="layout.web.tsx">
          <YStack justifyContent="center" alignItems="center" alignSelf="flex-start" onPress={() => router.push("/")} $gtSm={{
          pl: "$4.5"
        }} minHeight={54} data-sentry-element="YStack" data-sentry-source-file="layout.web.tsx">
            <GrantedTextLogo width={80} height={16} cursor="pointer" data-sentry-element="GrantedTextLogo" data-sentry-source-file="layout.web.tsx" />
          </YStack>
          {!hideTabs ? <>
              <YStack $sm={{
            display: "none"
          }}>
                <NavTabs orientation="horizontal" size="$4" />
              </YStack>
              <YStack $gtSm={{
            display: "none"
          }}>
                <MobileNavPopover contentPadding="$4">
                  <YStack gap="$5" width="100%" alignItems="flex-end">
                    <NavTabs orientation="vertical" f={1} width="100%" size="$3" />
                    <Separator width="100%" />
                    {isAuthenticated ? <WithUserDetail ai="center" gap="$4" /> : null}
                  </YStack>
                </MobileNavPopover>
              </YStack>
            </> : null}
        </XStack>
      </YStack>

      <YStack {...fullPage && {
      flex: 1
    }} {...padded && {
      maxWidth: 800,
      mx: "auto",
      px: "$2",
      width: "100%"
    }} data-sentry-element="YStack" data-sentry-source-file="layout.web.tsx">
        {children}
      </YStack>
    </YStack>;
};
const WithUserDetail = ({
  ...props
}: StackProps) => {
  const {
    user
  } = useAuth();
  return <XStack gap="$2" {...props} pb="$2" data-sentry-element="XStack" data-sentry-component="WithUserDetail" data-sentry-source-file="layout.web.tsx">
      <YStack ai="center" data-sentry-element="YStack" data-sentry-source-file="layout.web.tsx">
        <Paragraph theme="alt1" data-sentry-element="Paragraph" data-sentry-source-file="layout.web.tsx">{user?.profile?.email}</Paragraph>
      </YStack>
      <UserCircle data-sentry-element="UserCircle" data-sentry-source-file="layout.web.tsx" />
    </XStack>;
};